import guidesData from '../../data/embedCodePlatformGuides.json';
import { getNoCodePlatformOptions } from './noCodePlatformVisuals';

export type EmbedGuideStep = {
  title: string;
  description: string;
  code?: string;
  link?: string;
  linkText?: string;
  linkInternal?: boolean;
};

export type EmbedPlatformGuide = {
  id: string;
  name: string;
  readySubtitle: string;
  steps: EmbedGuideStep[];
  embedScript?: string;
};

type GuidesFile = {
  platforms: Record<string, EmbedPlatformGuide>;
};

const guides = guidesData as GuidesFile;

export type EmbedCredentials = {
  organizationId: string;
  appId: string;
  variantId: string;
  secretKey?: string;
};

const fillTemplate = (text: string, values: EmbedCredentials): string =>
  text
    .replace(/\{\{organizationId\}\}/g, values.organizationId || 'YOUR_ORGANIZATION_ID')
    .replace(/\{\{appId\}\}/g, values.appId || 'YOUR_APP_ID')
    .replace(/\{\{variantId\}\}/g, values.variantId || 'YOUR_VARIENT_ID')
    .replace(/\{\{secretKey\}\}/g, values.secretKey || 'YOUR_SECRET_KEY');

export const getEmbedPlatformGuide = (platformId: string): EmbedPlatformGuide => {
  const fromJson = guides.platforms[platformId];
  if (fromJson) return fromJson;

  const fallbackName =
    getNoCodePlatformOptions().find((opt) => opt.id === platformId)?.name || 'Platform';

  return {
    id: platformId,
    name: fallbackName,
    readySubtitle: `Review your credentials and follow the ${fallbackName} setup steps.`,
    steps: guides.platforms.others?.steps || [],
    embedScript: guides.platforms.others?.embedScript,
  };
};

export const fillEmbedGuideSteps = (
  guide: EmbedPlatformGuide,
  credentials: EmbedCredentials
): EmbedGuideStep[] =>
  guide.steps.map((step) => ({
    ...step,
    code: step.code ? fillTemplate(step.code, credentials) : undefined,
  }));

export const buildIntegrationSettingsJson = (
  platformName: string,
  credentials: EmbedCredentials
): string =>
  JSON.stringify(
    {
      platform: platformName,
      appId: credentials.appId || 'YOUR_APP_ID',
      secretKey: credentials.secretKey || 'YOUR_SECRET_KEY',
      varientId: credentials.variantId || 'YOUR_VARIENT_ID',
    },
    null,
    2
  );

export const fillEmbedScript = (
  guide: EmbedPlatformGuide,
  credentials: EmbedCredentials
): string => fillTemplate(guide.embedScript || '', credentials);
